import { useContext, useState } from 'react'
import {
  Dialog,
  DialogBackdrop,
  DialogPanel,
  Menu,
  MenuButton,
  MenuItem,
  MenuItems,
  TransitionChild,
} from '@headlessui/react'
import {
  Bars3Icon,
  XMarkIcon,
  EnvelopeIcon
} from '@heroicons/react/24/outline'
import { ChevronDownIcon } from '@heroicons/react/20/solid'
import DashboardIcon from './icons/dashboard'
import CustomersIcon from './icons/customers'
import ReportsIcon from './icons/reports'
import { Link, Route, Routes, useLocation, useNavigate } from 'react-router-dom'
import HRDashboard from './Dashboard/HRDashboard'
import HREmployees from './Employee/HREmployees'
import HRReports from './Reports/HRReports'
import { UserDataContext } from './context/UserContextProvider'
import HRSettings from './Settings/HRSettings'
import { useTranslation } from 'react-i18next'
import SelectWeekDashboardComponent from './components/selectWeekDashboardComponent'

function classNames(...classes) {
  return classes.filter(Boolean).join(' ')
}

export default function HRMain() {

  const { t } = useTranslation();

  const navigation = [
    { name: t('navigation.dashboard'), href: '/', icon: DashboardIcon },
    { name: t('navigation.employees'), href: '/employees', icon: CustomersIcon },
    { name: t('navigation.reports'), href: '/reports', icon: ReportsIcon },
  ]

  const location = useLocation();

  const [sidebarOpen, setSidebarOpen] = useState(false)

  const navigate = useNavigate()
  const userContext = useContext(UserDataContext)

  const logOut = () => {
    userContext.setUserData({})
    navigate('/login')
  }

  const [selectedWeek, setSelectedWeek] = useState("")

  const onSelectWeek = (week) => {
    setSelectedWeek(week)
  }

  return (
    <>
      <div>
        <Dialog open={sidebarOpen} onClose={setSidebarOpen} className="relative z-50 lg:hidden">
          <DialogBackdrop
            transition
            className="fixed inset-0 bg-gray-900/80 transition-opacity duration-300 ease-linear data-[closed]:opacity-0"
          />

          <div className="fixed inset-0 flex">
            <DialogPanel
              transition
              className="relative mr-16 flex w-full max-w-xs flex-1 transform transition duration-300 ease-in-out data-[closed]:-translate-x-full"
            >
              <TransitionChild>
                <div className="absolute left-full top-0 flex w-16 justify-center pt-5 duration-300 ease-in-out data-[closed]:opacity-0">
                  <button type="button" onClick={() => setSidebarOpen(false)} className="-m-2.5 p-2.5">
                    <span className="sr-only">Close sidebar</span>
                    <XMarkIcon aria-hidden="true" className="size-6 text-white" />
                  </button>
                </div>
              </TransitionChild>
              {/* Sidebar component, swap this element with another sidebar if you like */}
              <div className="flex grow flex-col gap-y-5 overflow-y-auto bg-white px-6">
                <div className="flex h-40 shrink-0 items-center">
                  <img
                    alt="TeamUp Logo"
                    src="/logo.svg"
                    className="h-32 w-auto"
                  />
                </div>
                <nav className="flex flex-1 flex-col mt-[-50px]">
                  <ul role="list" className="flex flex-1 flex-col gap-y-7">
                    <li>
                      <ul role="list" className="-mx-2 space-y-1">
                        {navigation.map((item) => (
                          <li key={item.name}>
                            <Link
                              to={item.href}
                              className={classNames(
                                (location.pathname.startsWith(item.href) && location.pathname !== '/' && item.href !== '/') || (location.pathname === '/' && item.href === '/')
                                  ? 'bg-primary text-white'
                                  : 'text-neutral hover:bg-primary hover:text-white',
                                'group flex gap-x-3 rounded-md p-2 text-sm/6 font-semibold',
                              )}
                            >
                              <item.icon
                                className={classNames(
                                  (location.pathname.startsWith(item.href) && location.pathname !== '/' && item.href !== '/') || (location.pathname === '/' && item.href === '/') ? 'fill-white' : 'fill-neutral group-hover:fill-white',
                                  'size-6 shrink-0',
                                )}
                              />
                              {item.name}
                            </Link>
                          </li>
                        ))}
                      </ul>
                    </li>
                  </ul>
                </nav>
              </div>
            </DialogPanel>
          </div>
        </Dialog>

        {/* Static sidebar for desktop */}
        <div className="hidden lg:fixed lg:inset-y-0 lg:z-50 lg:flex lg:w-72 lg:flex-col">
          {/* Sidebar component, swap this element with another sidebar if you like */}
          <div className="flex grow flex-col gap-y-5 overflow-y-auto bg-white px-6">
            <div className="flex h-40 shrink-0 items-center">
              <img
                alt="TeamUp Logo"
                src="/logo.svg"
                className="h-32 w-auto"
              />
            </div>
            <nav className="flex flex-1 flex-col mt-[-50px]">
              <ul role="list" className="flex flex-1 flex-col gap-y-7">
                <li>
                  <ul role="list" className="-mx-2 space-y-1">
                    {navigation.map((item) => (
                      <li key={item.name}>
                        <Link
                          to={item.href}
                          className={classNames(
                            (location.pathname.startsWith(item.href) && location.pathname !== '/' && item.href !== '/') || (location.pathname === '/' && item.href === '/')
                              ? 'bg-primary text-white'
                              : 'text-neutral hover:bg-primary hover:text-white',
                            'group flex gap-x-3 rounded-md p-2 text-sm/6 font-semibold',
                          )}
                        >
                          <item.icon
                            className={classNames(
                              (location.pathname.startsWith(item.href) && location.pathname !== '/' && item.href !== '/') || (location.pathname === '/' && item.href === '/') ? 'fill-white' : 'fill-neutral group-hover:fill-white',
                              'size-6 shrink-0',
                            )}
                          />
                          {item.name}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </li>
              </ul>
            </nav>
          </div>
        </div>

        <div className="lg:pl-72">
          <div className="sticky top-0 z-40 flex h-16 shrink-0 items-center gap-x-4 border-b border-gray-200 bg-white px-4 sm:gap-x-6 sm:px-6 lg:px-8 shadow-md">
            <button type="button" onClick={() => setSidebarOpen(true)} className="-m-2.5 p-2.5 text-gray-700 lg:hidden">
              <span className="sr-only">Open sidebar</span>
              <Bars3Icon aria-hidden="true" className="size-6" />
            </button>

            {location.pathname === '/' && <SelectWeekDashboardComponent onSelectWeek={onSelectWeek} />}

            <div className="flex flex-1 gap-x-4 self-stretch lg:gap-x-6">
              <div className="flex flex-1 items-center justify-end">
                <a href={`mailto:${t('navigation.supportEmail')}`} className="inline-flex md:hidden items-center justify-center text-primary hover:text-primary-dark" title={t('navigation.supportEmail')}>
                  <EnvelopeIcon className="w-5 h-5" />
                </a>
                <span className="hidden md:inline-flex items-center gap-1.5 text-sm text-base-content/50">
                  <EnvelopeIcon className="w-4 h-4" />
                  {t('navigation.supportText')}{' '}
                  <a href={`mailto:${t('navigation.supportEmail')}`} className="font-medium text-primary hover:underline">
                    {t('navigation.supportEmail')}
                  </a>
                </span>
              </div>
              <div className="flex items-center gap-x-4 lg:gap-x-6">

                {/* Profile dropdown */}
                <Menu as="div" className="relative">
                  <MenuButton className="-m-1.5 flex items-center p-1.5">
                    <span className="sr-only">Open user menu</span>
                    <img
                      alt=""
                      src="https://static.vecteezy.com/system/resources/thumbnails/001/840/618/small/picture-profile-icon-male-icon-human-or-people-sign-and-symbol-free-vector.jpg"
                      className="size-8 rounded-full bg-gray-50"
                    />
                    <span className="hidden lg:flex lg:items-center">
                      <span aria-hidden="true" className="ml-4 text-sm/6 font-semibold text-gray-900">
                        {userContext.user?.firstName} {userContext.user?.lastName}
                      </span>
                      <ChevronDownIcon aria-hidden="true" className="ml-2 size-5 text-gray-400" />
                    </span>
                  </MenuButton>
                  <MenuItems
                    transition
                    className="absolute right-0 z-10 mt-2.5 w-32 origin-top-right rounded-md bg-white py-2 shadow-lg ring-1 ring-gray-900/5 transition focus:outline-none data-[closed]:scale-95 data-[closed]:transform data-[closed]:opacity-0 data-[enter]:duration-100 data-[leave]:duration-75 data-[enter]:ease-out data-[leave]:ease-in"
                  >
                    <MenuItem>
                      <Link
                        to={'/settings'}
                        className="block px-3 py-1 text-sm/6 text-gray-900 data-[focus]:bg-gray-50 data-[focus]:outline-none"
                      >
                        {t('navigation.settings')}
                      </Link>
                    </MenuItem>
                    <MenuItem>
                      <span
                        className="block px-3 py-1 text-sm/6 text-gray-900 data-[focus]:bg-gray-50 data-[focus]:outline-none cursor-pointer"
                        onClick={() => logOut()}
                      >
                        {t('navigation.logout')}
                      </span>
                    </MenuItem>
                  </MenuItems>
                </Menu>
              </div>
            </div>
          </div>

          <main className="bg-neutral-100 h-full w-full min-h-screen">
            <div className="px-4 sm:px-6 lg:px-8 py-12">
              <Routes>
                <Route path="/reports" element={<HRReports />} />
                <Route path="/employees" element={<HREmployees />} />
                <Route path="/settings" element={<HRSettings />} />
                <Route path="/" element={<HRDashboard selectedWeek={selectedWeek} />} />
              </Routes>
            </div>
          </main>
        </div>
      </div>
    </>
  )
}